/**
 * Cardo — Weather Service
 *
 * Fetches live weather from Open-Meteo and geocodes locations.
 * This runs client-side for the MVP dashboard. In production,
 * the backend handles this via the WeatherProvider interface.
 */

import type { WeatherData } from "./types";

const WEATHER_URL = "https://api.open-meteo.com/v1/forecast";
const GEOCODE_URL = "https://geocoding-api.open-meteo.com/v1/search";

export interface GeoResult {
  lat: number;
  lng: number;
  display: string;
  short: string;
}

export async function fetchWeather(
  lat: number,
  lng: number,
): Promise<WeatherData> {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    current: [
      "temperature_2m",
      "apparent_temperature",
      "wind_speed_10m",
      "wind_gusts_10m",
      "precipitation_probability",
      "rain",
      "snowfall",
      "visibility",
    ].join(","),
    temperature_unit: "celsius",
    wind_speed_unit: "kmh",
    timezone: "auto",
  });

  const resp = await fetch(`${WEATHER_URL}?${params}`);
  if (!resp.ok) throw new Error(`Weather API: ${resp.status}`);
  const data = await resp.json();
  const c = data.current;

  return {
    temperature_f: Math.round(c.temperature_2m * (9 / 5) + 32),
    wind_speed_mph: Math.round((c.wind_speed_10m ?? 0) * 0.621371),
    wind_gust_mph: c.wind_gusts_10m
      ? Math.round(c.wind_gusts_10m * 0.621371)
      : null,
    precipitation_probability: c.precipitation_probability ?? 0,
    visibility_miles:
      Math.round(((c.visibility ?? 16000) / 1609.34) * 10) / 10,
    lightning_risk: false,
  };
}

export async function geocode(name: string): Promise<GeoResult[]> {
  const resp = await fetch(
    `${GEOCODE_URL}?name=${encodeURIComponent(name)}&count=5&language=en`,
  );
  if (!resp.ok) return [];
  const data = await resp.json();
  return (data.results ?? []).map(
    (x: {
      latitude: number;
      longitude: number;
      name: string;
      admin1?: string;
      country?: string;
    }) => ({
      lat: x.latitude,
      lng: x.longitude,
      display: [x.name, x.admin1, x.country].filter(Boolean).join(", "),
      short: x.admin1 ? `${x.name}, ${x.admin1}` : x.name,
    }),
  );
}

/** Fallback weather when API is unreachable */
export const FALLBACK_WEATHER: WeatherData = {
  temperature_f: 55,
  wind_speed_mph: 8,
  wind_gust_mph: null,
  precipitation_probability: 10,
  visibility_miles: 10,
  lightning_risk: false,
};
